/**
 * `jdbc:sqlserver://[serverName[\instanceName][:portNumber]][;property=value…]` → mssql (split out of `parseJdbc.ts`).
 *
 * The properties are read with the driver's own syntax (C3-01, r3 C3-15): braced values are read whole, `}}` is `}` —
 * `password={prefix;databaseName=X}` is one password; quotes are ordinary characters. A repeated property or a synonym is
 * read as mssql-jdbc reads it: the last instance (`repeatedKeywords.ts`).
 *
 * The target (review rounds 5 and 6): mssql-jdbc stores the URL's server, instance and port as the `serverName`,
 * `instanceName` and `portNumber` properties before it reads the `;`-properties (`Util.parseUrl`), so a `serverName` /
 * `server`, `portNumber` / `port` or `instanceName` property overrides the URL's ("Building the connection URL" documents
 * `jdbc:sqlserver://;serverName=…`; "Setting the connection properties" lists the synonyms). A blank one names none.
 * The instance is the `instanceName` (the URL's `\instance` or the property), else the one written into the FINAL
 * `serverName` (`host\SQLEXPRESS`) — and an explicit port drops it: "If you use both a portNumber and instanceName, the
 * portNumber takes precedence and the instanceName is ignored" ("Building the connection URL"). The import sources read
 * URLs with a copy of this (`electron/connection-import/shared/jdbcUrl.ts` `sqlServerTarget`);
 * `src/test/coupling/repeatedKeywordsCoupling.test.ts` compares the two.
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ParserMatch } from './types';
import { parseBoolish, isAzureSqlHost, applyMssqlTls, noteMssqlTls, type MssqlTlsValues } from './helpers';
import { scanDotNetConnectionString, type KeyValuePair } from '@/core/utils/connectionStringTokens';
import { CREDENTIAL_KEYS } from './connectionStringKeywords';
import { credentialsIn, withoutPlaceholders } from './placeholders';
import { effectivePairs, keywordOf, MSSQL_KEYWORD_RULES } from './repeatedKeywords';

/** The properties that name the target (synonyms folded by `keywordOf`). */
const TARGET_KEYWORDS: ReadonlySet<string> = new Set(['servername', 'portnumber', 'instancename']);

/** `host\instance` → its parts. */
function splitInstance(server: string): { host: string; instance?: string } {
  const slash = server.indexOf('\\');
  return slash === -1 ? { host: server } : { host: server.slice(0, slash), instance: server.slice(slash + 1) || undefined };
}

/** The URL's `serverName[\instanceName][:portNumber]`, as the properties mssql-jdbc stores it as. */
function authorityPairs(authority: string): KeyValuePair[] {
  const url = /^([^:]+)(?::(\d+))?$/u.exec(authority.trim());
  const { host, instance } = splitInstance(url?.[1] ?? '');
  const parts = { servername: host, instancename: instance, portnumber: url?.[2] };
  return Object.entries(parts).flatMap(([key, value]) => (value ? [{ key, value }] : []));
}

/**
 * The server (`host\instance` when one is named) and port mssql-jdbc dials from its target properties, in the order it
 * stores them: the last non-blank `serverName`, `instanceName` and `portNumber` (synonyms folded). The instance is the
 * `instanceName`, else the one in that `serverName` (`SQLServerConnection.connectInternal`: the property "takes
 * precedence"); an explicit port drops it — 0 is none (the driver then asks SQL Browser for the instance's port).
 */
function sqlServerTarget(pairs: readonly KeyValuePair[]): { server?: string; port?: string } {
  const last = (keyword: string) =>
    pairs.reduce<string | undefined>((found, { key, value }) => (keywordOf('jdbc', key) === keyword && value.trim() ? value.trim() : found), undefined);
  const { host, instance: inServerName } = splitInstance(last('servername') ?? '');
  const port = Number(last('portnumber')) === 0 ? undefined : last('portnumber');
  const instance = port ? undefined : last('instancename') ?? inServerName;
  return { server: host ? (instance ? `${host}\\${instance}` : host) : undefined, port };
}

/** `rest`: the URL after `jdbc:sqlserver://`. */
export function parseJdbcSqlServer(rest: string): ParserMatch | null {
  const values: Partial<ConnectionFormValues> = { engine: 'mssql' };
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];

  const firstSemicolon = rest.indexOf(';');
  const authority = firstSemicolon === -1 ? rest : rest.slice(0, firstSemicolon);
  const scanned = firstSemicolon === -1 ? [] : scanDotNetConnectionString(rest.slice(firstSemicolon + 1), 'odbc');
  // The driver rejects a malformed property list (an unterminated brace, text after a closing one): so is the string.
  if (!scanned) return null;
  // A portal placeholder (`password={your_password_here}`) is a credential not given (placeholders.ts).
  const properties = withoutPlaceholders(scanned, credentialsIn(CREDENTIAL_KEYS['jdbc-sqlserver']), MSSQL_KEYWORD_RULES.jdbc.rule);
  // A property value opening with a quote: the driver reads it literally (only braces quote), so the string is refused.
  const opened = properties.find((property) => property.opening);
  if (opened?.opening) return { format: 'jdbc', engine: 'mssql', values: {}, matchedFields: [], unmatchedKeys: [], opening: opened.opening };
  // So `integratedSecurity=true;integratedSecurity=false` is SQL Server authentication.
  const effective = effectivePairs(properties, 'jdbc');

  const { server, port } = sqlServerTarget([...authorityPairs(authority), ...properties]);
  if (server) {
    values.server = server;
    matchedFields.push('server');
  }
  if (port) {
    values.port = port;
    matchedFields.push('port');
  }

  const tls: MssqlTlsValues = {};
  for (const { key, value } of effective) {
    const keyword = keywordOf('jdbc', key);
    if (TARGET_KEYWORDS.has(keyword)) continue;
    if (keyword === 'databasename') {
      values.database = value;
      matchedFields.push('database');
    } else if (keyword === 'user') {
      values.login = value;
      matchedFields.push('user');
    } else if (keyword === 'password') {
      values.password = value;
      matchedFields.push('password');
    } else if (keyword === 'encrypt') {
      noteMssqlTls(tls, 'encrypt', value);
    } else if (keyword === 'trustservercertificate') {
      noteMssqlTls(tls, 'trust', value);
    } else if (keyword === 'integratedsecurity') {
      if (parseBoolish(value) === true) {
        values.authentication = 'Windows authentication';
        matchedFields.push('authentication');
      }
    } else {
      unmatchedKeys.push(key);
    }
  }

  // Encrypt=Strict validates the certificate: Trust server certificate off, whatever the URL says (mssqlEncrypt.ts); a
  // value the driver rejects fills neither and refuses the URL (`tlsUnreadable`, review round 5).
  const { strict, unreadable } = applyMssqlTls(tls, values, matchedFields);
  const match: ParserMatch = { format: 'jdbc', engine: 'mssql', values, matchedFields, unmatchedKeys };
  if (strict) match.encryptStrict = true;
  if (unreadable) match.tlsUnreadable = unreadable;
  if (isAzureSqlHost(values.server) && !values.password && values.authentication !== 'Windows authentication') {
    match.suggestEntra = true;
  }
  return match;
}
